import type {
  ComponentPropsWithoutRef,
  ForwardRefRenderFunction,
  CSSProperties,
} from "react";
import { forwardRef, memo } from "react";
import { areEqual } from "react-window";
import type { Account } from "../../../../portals/messagemedia/hooks";
import { useAccountSwitcherContext } from "../../AccountSwitcherContext";
import { AccountSwitcherNode } from "../AccountSwitcherNode/AccountSwitcherNode";
import { AccountSwitcherSearch } from "../AccountSwitcherSearch/AccountSwitcherSearch";
import { AccountSwitcherSearchResult } from "../AccountSwitcherSearchResult/AccountSwitcherSearchResult";
import {
  StyledWrapper,
  StyledSearchContainer,
  StyledTreeContainer,
  StyledList,
  wrapperPadding,
  treeMaxHeight,
} from "./styled";

type Data = {
  isExpand: boolean;
  account: Account;
  level: number;
}[];
type NodeProps = {
  index: number;
  style: CSSProperties;
  data: unknown;
};
const Node = ({ index, style, data }: NodeProps) => {
  const typedData = data as Data;
  const isExpand = !!typedData?.[index]?.isExpand;
  const account = typedData?.[index]?.account || {
    accountId: "",
    accountName: "",
  };
  const depthLevel = typedData?.[index]?.level || 0;
  return (
    <AccountSwitcherNode
      index={index}
      isExpand={isExpand}
      style={style}
      key={account.accountId}
      accountId={account.accountId}
      accountName={account.accountName}
      subAccounts={account.children}
      depthLevel={depthLevel}
    />
  );
};
const MemoNode = memo(Node, areEqual);

export type AccountSwitcherTreeProps = ComponentPropsWithoutRef<"div">;
const AccountSwitcherTreeNoRef: ForwardRefRenderFunction<
  HTMLDivElement,
  AccountSwitcherTreeProps
> = ({ ...props }, ref) => {
  const { list, isSearchBarFocus } = useAccountSwitcherContext();

  const actualHeight = list.length * 40;
  /* istanbul ignore next */
  const renderHeight =
    actualHeight > treeMaxHeight
      ? treeMaxHeight
      : actualHeight + wrapperPadding;

  return (
    <StyledWrapper ref={ref} {...props}>
      <StyledSearchContainer>
        <AccountSwitcherSearch />
      </StyledSearchContainer>
      <AccountSwitcherSearchResult
        style={{ display: isSearchBarFocus ? "block" : "none" }}
      />
      {!isSearchBarFocus && (
        <StyledTreeContainer>
          <StyledList
            itemCount={list.length}
            itemSize={40}
            width={400}
            height={renderHeight}
            itemData={list}
          >
            {MemoNode}
          </StyledList>
        </StyledTreeContainer>
      )}
    </StyledWrapper>
  );
};
export const AccountSwitcherTree = forwardRef(AccountSwitcherTreeNoRef);
